import { LitElement, html, css } from 'lit';
import { property, query } from 'lit/decorators.js';

import './components/langhout-beton-login/langhout-beton-login';
import './components/langhout-beton-privacy-policy/langhout-beton-privacy-policy';
import './components/langhout-beton-home/langhout-beton-home';
import './components/langhout-beton-order-setup-done/langhout-beton-order-setup-done';
import './components/langhout-beton-order-loading-done/langhout-beton-order-loading-done';
import './components/langhout-beton-stock-count/langhout-beton-stock-count';
import './components/langhout-beton-stock-count-done/langhout-beton-stock-count-done';
import './components/langhout-beton-order-cleanup-done/langhout-beton-order-cleanup-done';
import './components/langhout-beton-production-unit-settings/langhout-beton-production-unit-settings';
import './components/langhout-beton-view-production-planning/langhout-beton-view-production-planning';
import './elements/langhout-button/langhout-button';

import './elements/StyleProvider';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { Router } from '@vaadin/router';
import { Capacitor } from '@capacitor/core';
import { authenticatedAPICall, currentVersion } from './client/api-client';
import { jwtDecode } from 'jwt-decode';
import {
  DOMAIN,
  JWT_KEY_LOCALSTORAGE,
  JWT_OTP_KEY_LOCALSTORAGE,
} from './environment/environment';
import {
  PushNotifications,
  PushNotificationSchema,
  Token,
} from '@capacitor/push-notifications';
import { Claims, ConfirmEvent, User } from './types/types';
import {
  archiveIcon,
  closeIcon,
  headerLogo100,
  checkSquareIcon,
  homeIcon,
  menuIcon,
  signInIcon,
  logOutIcon,
  userIcon,
  factoryIcon,
  settingsIcon,
  calendarIcon,
  binIcon,
} from './elements/icons/icons';
import {
  christmasStyles,
  CHRISTMAS_THEME_ENABLED,
} from './elements/christmas-theme';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export class LanghoutBetonApp extends LitElement {
  @property({ type: String }) title = 'Login';

  static styles = [
    christmasStyles,
    css`
      :host {
        margin-top: env(safe-area-inset-top);
        min-height: calc(100vh - env(safe-area-inset-top));
        max-height: calc(100vh - env(safe-area-inset-top));
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        font-size: 17px;
        color: var(--text-color-main);
        background-color: var(--background-color);
        text-align: center;
      }

      style-provider {
        min-width: 100vw;
      }

      .header {
        width: calc(100% - 2px);
        min-height: 82px;
        max-height: 82px;
        padding: 1px;
        background-color: var(--menu-color);
        color: var(--text-color-light);
        display: flex;
        flex-direction: row;
      }

      .headerLogoContainer {
        display: flex;
        flex-direction: row;
        justify-content: center;
        text-align: center;
        height: 82px;
        width: 100%;
        padding-left: 20px;
        padding-right: 20px;
      }

      .grow {
        flex-grow: 1;
      }

      .headerLogoWrapper {
        display: flex;
        flex-direction: column;
        justify-content: center;
        text-align: center;
        width: 100%;
        max-width: 319.6px;
      }

      #headerLogo {
        padding: 0px;
        height: auto;
        width: auto;
        max-height: 50px;
      }

      .menu-icon {
        display: flex;
        flex-direction: column;
        justify-content: center;
      }

      svg {
        padding: 20px;
        height: 30px;
        width: 30px;
      }

      #title {
        margin-left: 10px;
      }

      #title:hover {
        cursor: pointer;
      }

      h2 {
        font-size: 24px;
      }

      .page {
        max-height: calc(100vh - 144px - env(safe-area-inset-top));
        padding-top: 20px;
        padding-bottom: 40px;
        padding-left: 30px;
        padding-right: 30px;
        overflow-y: auto;
      }

      #menu {
        width: calc(100vw);
        height: calc(100% - 72px - env(safe-area-inset-top) - 20px);
        position: absolute;
        top: calc(72px + env(safe-area-inset-top));
        left: 0;
        z-index: 999;
        transform: translateX(-100vw);
        transition-duration: 400ms;
        background-color: var(--menu-color);
        color: var(--text-color-light);
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        padding-bottom: 20px;
      }

      :host([showMenu]) #menu {
        transition-duration: 500ms;
        transform: translateX(0);
      }

      #close-button {
        position: absolute;
        right: 0;
      }

      .menu-item {
        color: var(--text-color-light);
        font-size: 21px;
        font-weight: bold;
        text-decoration: none;
        text-align: left;
        display: flex;
        flex-direction: row;
      }

      .menu-text {
        display: flex;
        flex-direction: column;
        justify-content: center;
      }

      .version-text {
        padding-left: 70px;
      }

      #user-icon {
        cursor: initial;
      }

      #user-text {
        cursor: initial;
      }

      #modal {
        position: absolute;
        transform: translateY(-200px);
        transition-duration: 1s;

        left: 0;
        right: 0;
        margin-left: auto;
        margin-right: auto;
        width: 80vw;
        display: flex;
        flex-direction: column;
        align-items: center;
        z-index: 99999;
      }

      :host([notificationVisible]) #modal {
        transition-duration: 500ms;
        transform: translateY(20px);
      }

      .modal-content {
        color: var(--text-color-light);
        border-radius: 10px;
        background-color: var(--text-color-main);
        width: 80vw;
        height: auto;
        padding: 10px;
      }

      #versionModal {
        position: absolute;
        transform: translateY(calc(-100vh - env(safe-area-inset-top)));
        transition-duration: 1s;

        width: 100vw;
        height: calc(100vh - env(safe-area-inset-top));
        display: flex;
        flex-direction: column;
        justify-content: center;

        align-items: center;
        z-index: 9999;
      }

      .version-modal-content {
        color: var(--text-color-light);
        border-radius: 10px;

        background-color: var(--menu-color);
        box-shadow: 0px 0px 0px 1px var(--background-color);

        box-shadow:
          0.25rem 0.25rem 0.6rem rgba(0, 0, 0, 0.5),
          0 0.5rem 1.125rem rgba(75, 0, 0, 0.05);
        width: 80vw;
        padding: 10px;
        height: calc(100vh - 140px - env(safe-area-inset-top));
      }

      :host([versionPopupVisible]) #versionModal {
        transition-duration: 500ms;
        transform: translateY(0px);
      }

      a {
        color: var(--text-color-light);
        text-decoration: none;
      }

      #buttonWrapper {
        margin-top: 30px;
        display: flex;
        flex-direction: column;
        align-items: center;
      }

      #button {
        width: 70vw;
        background-color: var(--primary-color);
        padding: 12px;
        color: var(--text-color-light);
        border-radius: 10px;
        font-weight: bold;
        max-width: 300px;
      }

      #confirmModal {
        position: absolute;

        width: 100vw;
        height: 100vh;
        display: none;
        flex-direction: column;
        justify-content: center;

        align-items: center;
        z-index: 9999;
      }

      :host([confirmPopupVisible]) #confirmModal {
        display: flex;
        background-color: rgba(0, 0, 0, 0.6);
      }

      .confirm-modal-content {
        border-radius: 10px;

        color: var(--text-color-main);
        background-color: var(--background-color);
        box-shadow: 0px 0px 0px 1px white;

        box-shadow:
          0.25rem 0.25rem 0.6rem rgba(0, 0, 0, 0.5),
          0 0.5rem 1.125rem rgba(75, 0, 0, 0.05);
        padding-left: 40px;
        padding-right: 40px;
        padding-top: 15px;
        padding-bottom: 40px;
      }

      langhout-button {
        max-width: 200px;
        width: 200px;
        min-width: 180px;
        margin-right: 20px;
        margin-top: 20px;
      }

      .row {
        margin-top: 30px;
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        flex-flow: row wrap;
        justify-content: center;
      }
    `,
  ];

  @property({ type: Boolean, reflect: true })
  public confirmPopupVisible = false;

  @property({ type: Object, reflect: true })
  private confirmEvent: ConfirmEvent = <ConfirmEvent>{};

  @property({ type: Boolean, reflect: true })
  public notificationVisible?: boolean;

  @property({ type: Boolean, reflect: true })
  public versionPopupVisible = false;

  @property({ type: Boolean, reflect: true })
  public showMenu = false;

  @query('#outlet')
  private outletElem?: HTMLDivElement;

  private router?: Router;

  @property({ type: String, reflect: true })
  public message?: string;

  @property({ type: String, reflect: true })
  public version?: string;

  @query('#playback')
  public audioElement?: HTMLAudioElement;

  @property({ type: Object, reflect: true })
  public user?: User;

  private webSocket?: WebSocket;

  private backOff = 0;

  constructor() {
    super();
    this.refreshTokenCall();
    this.refreshOTPTokenCall();
    this.getUserCall();
    window.addEventListener(
      'vaadin-router-location-changed',
      this.routerLocationChanged as EventListener,
    );
    document.addEventListener(
      'show-notification',
      this.showNotification as EventListener,
    );
    document.addEventListener(
      'confirm-event',
      this.showConfirmPopup as EventListener,
    );
    this.notificationVisible = false;
    this.initPushNotifications();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      'vaadin-router-location-changed',
      this.routerLocationChanged as EventListener,
    );
    document.removeEventListener(
      'show-notification-event',
      this.showNotification as EventListener,
    );
    document.removeEventListener(
      'confirm-event',
      this.showConfirmPopup as EventListener,
    );
    if (this.webSocket) {
      this.webSocket.close();
    }
    if (Capacitor.getPlatform() === 'web') {
      return;
    }
    PushNotifications.removeAllListeners();
  }

  private showConfirmPopup = (e: CustomEvent): void => {
    this.confirmPopupVisible = true;
    this.confirmEvent = JSON.parse(e.detail.value);
  };

  private showNotification = (e: CustomEvent): void => {
    this.notificationVisible = true;
    this.message = e.detail.value;
    setTimeout(() => {
      this.notificationVisible = false;
    }, 5000);
  };

  protected firstUpdated(): void {
    if (!this.router && this.outletElem) {
      this.router = new Router(this.outletElem);
      this.router.setRoutes([
        { path: '/', component: 'langhout-beton-home' },
        { path: '/login', component: 'login-element' },
        { path: '/privacy-policy', component: 'langhout-beton-privacy-policy' },
        {
          path: '/ready-for-pickup',
          component: 'langhout-beton-order-setup-done',
        },
        {
          path: '/cleaned-up',
          component: 'langhout-beton-order-cleanup-done',
        },
        {
          path: '/loading-done',
          component: 'langhout-beton-order-loading-done',
        },
        {
          path: '/stock-count',
          component: 'langhout-beton-stock-count',
        },
        {
          path: '/stock-count-done',
          component: 'langhout-beton-stock-count-done',
        },
        {
          path: '/production-unit-settings',
          component: 'langhout-beton-production-unit-settings',
        },
        {
          path: '/production-planning',
          component: 'langhout-beton-view-production-planning',
        },
      ]);
    }
  }

  connectWebsocket = () => {
    if (!this.webSocket) {
      const jwtFromStorage = localStorage.getItem(JWT_KEY_LOCALSTORAGE);
      if (!jwtFromStorage) {
        setTimeout(() => {
          this.connectWebsocket();
        }, 100);
        return;
      }
      this.webSocket = new WebSocket(
        `wss://${DOMAIN}/pickuporders/websocket/app?token=Bearer ${jwtFromStorage}`,
      );
      this.webSocket.onclose = evt => {
        this.backOff++;
        console.warn('websocket closed', evt);
        this.webSocket = undefined;
        setTimeout(() => {
          this.connectWebsocket();
        }, 100 * this.backOff);
      };
      this.webSocket.onmessage = () => {
        this.backOff = 0;
        if (window.location.pathname === '/') {
          showNotification('Nieuwe inkomende pickorders');
        }
        refreshTasksEvent();
      };
      this.webSocket.onerror = evt => {
        console.warn('ERROR: ', evt);
      };
    }
  };

  protected routerLocationChanged = (): void => {
    const router: Router | undefined = this.router;
    if (!router) {
      console.log('no router', router);
      console.log('no outletElem', this.outletElem);
      return;
    }

    const jwt = localStorage.getItem(JWT_KEY_LOCALSTORAGE);
    if (
      !jwt &&
      !window.location.href.endsWith('/login') &&
      !window.location.href.endsWith('/privacy-policy')
    ) {
      window.location.href = '/login';
    }
    this.setTitle();
  };

  protected setTitle() {
    const path = window.location.pathname;
    if (path === '/login') {
      this.title = 'Login';
    } else if (path === '/loading-done') {
      this.title = 'Afgerond';
    } else if (path === '/ready-for-pickup') {
      this.title = 'Klaargezet';
    } else if (path === '/cleaned-up') {
      this.title = 'Opgeruimd';
    } else if (path === '/') {
      this.title = 'Overzicht';
    } else if (path === '/stock-count') {
      this.title = 'Productie';
    } else if (path === '/production-unit-settings') {
      this.title = 'Instellingen';
    } else if (path === '/stock-count-done') {
      this.title = 'Gelost';
    } else if (path === '/production-planning') {
      this.title = 'Planning';
    } else {
      this.title = 'Langhout';
    }
  }

  render() {
    return html`
      <audio id="playback">
        <source src="/assets/alarm.mp3" type="audio/mpeg" />
      </audio>
      <style-provider>
        ${CHRISTMAS_THEME_ENABLED ? this.renderSnowflakes() : ''}
        <div id="confirmModal">
          <div class="confirm-modal-content">
            <h1>${this.confirmEvent.title}</h1>
            <div>${unsafeHTML(this.confirmEvent.description)}</div>
            <div class="row">
              <langhout-button
                text="Annuleren"
                @click="${() => {
                  this.confirmPopupVisible = false;
                }}"
              ></langhout-button>
              <div class="buttonSpacing"></div>
              <langhout-button
                removeButton
                text="Bevestigen"
                @click="${() => {
                  document.dispatchEvent(
                    new CustomEvent(this.confirmEvent.confirm_event, {
                      detail: {},
                      bubbles: true,
                    }),
                  );
                  this.confirmPopupVisible = false;
                }}"
              ></langhout-button>
            </div>
          </div>
        </div>
        <!-- Version popup -->
        <div id="versionModal">
          <div class="version-modal-content">
            <h2>New version available</h2>
            <div>A new app version is available, please update your app.</div>
            <a
              target="_blank"
              href="https://play.google.com/store/apps/details?id=com.langhoutbeton.app"
            >
              <div id="buttonWrapper">
                <div id="button">Download version ${this.version}</div>
              </div>
            </a>
          </div>
        </div>
        <!-- Message popup -->
        <div id="modal">
          <div class="modal-content">${this.message}</div>
        </div>
        <!-- Menu -->
        <div id="menu">
          <div
            id="close-button"
            @click="${(): void => {
              this.showMenu = false;
            }}"
          >
            ${closeIcon()}
          </div>
          <a
            class="menu-item"
            href="/"
            @click="${(): void => {
              this.showMenu = false;
            }}"
          >
            ${homeIcon()}
            <div class="menu-text">Overzicht</div>
          </a>
          <a
            class="menu-item"
            href="/production-planning"
            @click="${(): void => {
              this.showMenu = false;
            }}"
          >
            ${calendarIcon()}
            <div class="menu-text">Productie planning</div>
          </a>
          <a
            class="menu-item"
            href="/stock-count"
            @click="${(): void => {
              this.showMenu = false;
            }}"
          >
            ${factoryIcon()}
            <div class="menu-text">Karren lossen</div>
          </a>
          <a
            class="menu-item"
            href="/production-unit-settings"
            @click="${(): void => {
              this.showMenu = false;
            }}"
          >
            ${settingsIcon()}
            <div class="menu-text">Machine instellingen</div>
          </a>
          <a
            class="menu-item"
            href="/ready-for-pickup"
            @click="${(): void => {
              this.showMenu = false;
            }}"
          >
            ${checkSquareIcon()}
            <div class="menu-text">Klaargezet</div>
          </a>
          <a
            class="menu-item"
            href="/cleaned-up"
            @click="${(): void => {
              this.showMenu = false;
            }}"
          >
            ${binIcon()}
            <div class="menu-text">Opgeruimd</div>
          </a>
          <a
            class="menu-item"
            href="/loading-done"
            @click="${(): void => {
              this.showMenu = false;
            }}"
          >
            ${archiveIcon()}
            <div class="menu-text">Afgerond</div>
          </a>
          <a
            class="menu-item"
            href="/stock-count-done"
            @click="${(): void => {
              this.showMenu = false;
            }}"
          >
            ${archiveIcon()}
            <div class="menu-text">Gelost</div>
          </a>
          ${this.showUserName()} ${this.showLoginItem()}
          <a class="menu-item">
            <div class="menu-text version-text">Versie: ${currentVersion}</div>
          </a>
        </div>

        <!-- Normal content -->
        <div
          class="header ${CHRISTMAS_THEME_ENABLED ? 'christmas-accent' : ''}"
        >
          <div
            class="menu-icon"
            @click="${(): void => {
              this.showMenu = !this.showMenu;
            }}"
          >
            ${menuIcon()}
          </div>
          <h2
            id="title"
            @click="${(): void => {
              window.location.href = '/';
            }}"
          >
            ${this.title}
          </h2>
          <div class="headerLogoContainer">
            <div class="grow"></div>
            <div class="headerLogoWrapper">${headerLogo100()}</div>
          </div>
        </div>
        <div class="page">
          <div id="outlet"></div>
        </div>
        <div class="footer"></div>
      </style-provider>
    `;
  }

  showUserName() {
    if (!this.user) {
      return html``;
    }
    return html`
      <div class="menu-item">
        ${userIcon()}
        <div id="user-text" class="menu-text">
          ${this.user.first_name} ${this.user.last_name}
        </div>
      </div>
    `;
  }

  renderSnowflakes() {
    return html`
      <div class="snowflake">❄</div>
      <div class="snowflake">❅</div>
      <div class="snowflake">❆</div>
      <div class="snowflake">❄</div>
      <div class="snowflake">❅</div>
      <div class="snowflake">❆</div>
      <div class="snowflake">❄</div>
      <div class="snowflake">❅</div>
      <div class="snowflake">❆</div>
      <div class="snowflake">❄</div>
    `;
  }

  showLoginItem() {
    const jwt = localStorage.getItem(JWT_OTP_KEY_LOCALSTORAGE);
    if (!jwt) {
      return html` <a
        class="menu-item"
        href="/login"
        @click="${(): void => {
          this.showMenu = false;
        }}"
      >
        ${signInIcon()}
        <div class="menu-text">Login</div>
      </a>`;
    }
    return html` <a
      class="menu-item"
      @click="${(): void => {
        this.showMenu = false;
        localStorage.clear();
        window.location.href = '/login';
      }}"
    >
      ${logOutIcon()}
      <div class="menu-text">Logout</div>
    </a>`;
  }

  initPushNotifications() {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }

    PushNotifications.requestPermissions().then(result => {
      if (result.receive === 'granted') {
        // Register with Apple / Google to receive push via APNS/FCM
        PushNotifications.register();
      } else {
        // Show some error
      }
    });

    // On success, we should be able to receive notifications
    PushNotifications.addListener('registration', (token: Token) => {
      this.registerPushNotification(token.value);
    });

    // Some issue with our setup and push will not work
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    PushNotifications.addListener('registrationError', (error: any) => {
      console.warn('Error on registration: ' + JSON.stringify(error));
    });

    PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        refreshTasksEvent();
        if (notification.title) {
          showNotification(notification.title);
        }
        this.playAlarm();
      },
    );
  }

  playAlarm = () => {
    if (!this.audioElement) {
      return;
    }
    this.audioElement.play();
  };

  registerPushNotification = async (token: string) => {
    authenticatedAPICall(
      '/push-notifications/register',
      'POST',
      new Headers(),
      JSON.stringify({
        registration_id: token,
        os_type: Capacitor.getPlatform(),
      }),
    );
  };

  refreshTokenCall = async () => {
    const result = await authenticatedAPICall(
      '/token/refresh',
      'POST',
      new Headers(),
    );
    if (result && result.statusCode == 401) {
      if (
        !window.location.href.endsWith('/login') &&
        !window.location.href.endsWith('/privacy-policy')
      ) {
        this.showMenu = false;
        localStorage.clear();
        window.location.href = '/login';
      }
      return;
    }
    const body = result.body;
    if (isRecord(body) && typeof body.access_token === 'string') {
      localStorage.setItem(JWT_KEY_LOCALSTORAGE, body.access_token);
      this.connectWebsocket();
    }
  };

  getUserCall = async () => {
    const jwt = localStorage.getItem(JWT_KEY_LOCALSTORAGE);
    if (!jwt) {
      return;
    }
    const claims: Claims = jwtDecode(jwt);
    const result = await authenticatedAPICall(
      `/users/${claims.sub}`,
      'GET',
      new Headers(),
    );
    if (result && result.statusCode !== undefined && result.statusCode >= 200 && result.statusCode < 300 && isRecord(result.body)) {
      this.user = result.body as User;
    }
  };

  refreshOTPTokenCall = async () => {
    const result = await authenticatedAPICall(
      '/token/refresh/otp',
      'POST',
      new Headers(),
      null,
      JWT_OTP_KEY_LOCALSTORAGE,
    );
    const body = result.body;
    if (isRecord(body) && typeof body.access_token === 'string') {
      localStorage.setItem(JWT_OTP_KEY_LOCALSTORAGE, body.access_token);
    } else if (result && result.statusCode == 401) {
      localStorage.removeItem(JWT_OTP_KEY_LOCALSTORAGE);
    }
  };

  latestVersionCall = async () => {
    const result = await authenticatedAPICall(
      '/version/app',
      'GET',
      new Headers(),
    );
    const body = result.body;
    if (!isRecord(body) || typeof body.version !== 'string') {
      return;
    }
    this.version = body.version;
    if (!this.isLatestVersion(body.version, currentVersion)) {
      this.versionPopupVisible = true;
    }
  };

  isLatestVersion = (backendVersion: string, appVersion: string): boolean => {
    backendVersion = backendVersion.substring(1);
    appVersion = appVersion.substring(1);
    const appVersionSplitted = appVersion.split('.');
    const backendVersionSplitted = backendVersion.split('.');
    if (appVersionSplitted.length !== backendVersionSplitted.length) {
      console.warn('could not compare versions');
      return true;
    }
    for (let i = 0; i < appVersionSplitted.length; i++) {
      if (
        parseInt(appVersionSplitted[i]) > parseInt(backendVersionSplitted[i])
      ) {
        return true;
      }
      if (
        parseInt(backendVersionSplitted[i]) > parseInt(appVersionSplitted[i])
      ) {
        return false;
      }
    }
    return true;
  };
}

export const refreshTasksEvent = () => {
  const refreshTasksEvent = new CustomEvent('refresh-tasks', {
    detail: {
      value: 'refresh-tasks',
    },
    bubbles: true,
  });
  document.dispatchEvent(refreshTasksEvent);
};

export const notificationEvent: (message: string) => CustomEvent = (
  message: string,
): CustomEvent => {
  return new CustomEvent('show-notification', {
    detail: {
      value: message,
    },
    bubbles: true,
  });
};

export const showNotification: (message: string) => void = (
  message: string,
): void => {
  document.dispatchEvent(notificationEvent(message));
};

export const confirmEventEvent: (confirmEvent: ConfirmEvent) => CustomEvent = (
  confirmEvent: ConfirmEvent,
): CustomEvent => {
  return new CustomEvent('confirm-event', {
    detail: {
      value: JSON.stringify(confirmEvent),
    },
    bubbles: true,
  });
};

export const confirmPopup: (confirmEvent: ConfirmEvent) => void = (
  confirmEvent: ConfirmEvent,
): void => {
  document.dispatchEvent(confirmEventEvent(confirmEvent));
};
